import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import { API_BASE_URL } from '../utils/api';
import { isAdmin } from '../utils/permissions';
import TaskModal from '../components/TaskModal';
import PhotoUpload from '../components/PhotoUpload';
import { User, Task } from '../types';

const Archived: React.FC = () => {
  const { user } = useAuth();
  const [tasks, setTasks] = useState<Task[] | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [engineerFilter, setEngineerFilter] = useState<number | 'all'>('all');
  const [collapsedDates, setCollapsedDates] = useState<Set<string>>(new Set());
  const [expandedNotes, setExpandedNotes] = useState<Set<number>>(new Set());
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | undefined>();
  const [isPhotoModalOpen, setIsPhotoModalOpen] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');

  const userIsAdmin = isAdmin(user);

  useEffect(() => {
    fetchTasks();
    fetchUsers();
  }, []);

  // Set default filter based on user type
  useEffect(() => {
    if (user && userIsAdmin) {
      setEngineerFilter('all');
    }
  }, [user, userIsAdmin]);

  const fetchTasks = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/tasks`);
      console.log('Archived tasks API response:', response.data);
      const taskData = response.data;
      if (Array.isArray(taskData)) {
        // Only show invoiced tasks
        const invoicedTasks = taskData.filter(task => task.status?.toLowerCase() === 'invoiced');
        setTasks(invoicedTasks);
      } else {
        console.warn('Tasks API returned non-array data:', taskData);
        setTasks([]);
      }
    } catch (err: any) {
      console.error('Error fetching archived tasks:', err);
      setError('Failed to fetch archived tasks');
      setTasks([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/users`);
      const userData = response.data;
      if (Array.isArray(userData)) {
        setUsers(userData.filter((u: any) => u && u.user_type === 'engineer'));
      } else {
        console.warn('Users API returned non-array data:', userData);
        setUsers([]);
      }
    } catch (err: any) {
      console.error('Failed to fetch users:', err);
      setUsers([]);
    }
  };

  const toggleDateCollapse = (date: string) => {
    setCollapsedDates(prev => {
      const newSet = new Set(prev);
      if (newSet.has(date)) {
        newSet.delete(date);
      } else {
        newSet.add(date);
      }
      return newSet;
    });
  };

  const toggleNotesExpansion = (taskId: number) => {
    setExpandedNotes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(taskId)) {
        newSet.delete(taskId);
      } else {
        newSet.add(taskId);
      }
      return newSet;
    });
  };

  const shouldTruncateNotes = (notes: string) => {
    if (!notes) return false;
    const lines = notes.split('\n');
    return lines.length > 2 || notes.length > 100;
  };

  const getTruncatedNotes = (notes: string) => {
    if (!notes) return '';
    const lines = notes.split('\n');
    if (lines.length <= 2) {
      return notes.length > 100 ? notes.substring(0, 100) + '...' : notes;
    }
    return lines.slice(0, 2).join('\n') + '...';
  };

  const handleEditTask = (task: Task) => {
    setEditingTask(task);
    setIsModalOpen(true);
  };

  const handleViewPhotos = (taskId: number) => {
    setSelectedTaskId(taskId);
    setIsPhotoModalOpen(true);
  };

  const handleUpdateTask = async (taskData: any) => {
    try {
      await axios.put(`${API_BASE_URL}/api/tasks/${taskData.id}`, taskData);
      setIsModalOpen(false);
      setEditingTask(undefined);
      fetchTasks(); // Refresh the archived tasks
    } catch (err: any) {
      setError('Failed to update task');
    }
  };

  const handleTaskSubmit = (taskData: any) => {
    if (editingTask) {
      handleUpdateTask({ ...taskData, id: editingTask.id });
    }
    // Note: We don't handle creation here since this is the archived page
  };


  if (loading) {
    return (
      <div>
        <Header />
        <div className="container">
          <div style={{ textAlign: 'center', padding: '2rem' }}>Loading archived tasks...</div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Header />
      <div className="container">
        <h1 className="page-title">Archived Tasks (Invoiced)</h1>

        {error && <div className="error-message">{error}</div>}

        {/* Search and Filter Controls */}
        <div style={{ 
          marginBottom: '1.5rem', 
          display: 'flex', 
          alignItems: 'center', 
          gap: '1rem',
          padding: '1rem',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px',
          border: '1px solid #e9ecef',
          flexWrap: 'wrap'
        }}>
          {/* Search Input */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', minWidth: '250px' }}>
            <label htmlFor="search-input" style={{ fontWeight: '500', color: '#495057', whiteSpace: 'nowrap' }}>
              🔍 Search:
            </label>
            <input
              id="search-input"
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by title, client, or address..."
              style={{
                padding: '0.5rem',
                borderRadius: '4px',
                border: '1px solid #ced4da',
                backgroundColor: 'white',
                fontSize: '0.9rem',
                flex: 1,
                minWidth: '200px'
              }}
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '1.2rem',
                  cursor: 'pointer',
                  color: '#6c757d',
                  padding: '0.25rem'
                }}
                title="Clear search"
              >
                ✕
              </button>
            )}
          </div>

          {/* Engineer Filter */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', minWidth: '200px' }}>
            <label htmlFor="engineer-filter" style={{ fontWeight: '500', color: '#495057', whiteSpace: 'nowrap' }}>
              Filter by Engineer:
            </label>
          <select
            id="engineer-filter"
            value={engineerFilter}
            onChange={(e) => {
              const value = e.target.value;
              if (value === 'all') {
                setEngineerFilter('all');
              } else {
                setEngineerFilter(parseInt(value));
              }
            }}
            style={{
              padding: '0.5rem 1rem',
              borderRadius: '4px',
              border: '1px solid #ced4da',
              backgroundColor: 'white',
              fontSize: '0.9rem',
              minWidth: '200px'
            }}
          >
            <option value="all">All Engineers</option>
            {users.map(engineer => (
              <option key={engineer.id} value={engineer.id}>
                {engineer.first_name} {engineer.last_name}
              </option>
            ))}
          </select>
          </div>
        </div>

        {(() => {
          const safeTasks = Array.isArray(tasks) ? tasks : [];
          
          // Filter tasks based on search and engineer selection
          const filteredTasks = safeTasks.filter(task => {
            if (!task || !task.id) return false;
            
            // Search filter
            if (searchTerm.trim()) {
              const searchLower = searchTerm.toLowerCase().trim();
              const matchesSearch = 
                task.title?.toLowerCase().includes(searchLower) ||
                task.client?.toLowerCase().includes(searchLower) ||
                task.address_line1?.toLowerCase().includes(searchLower) ||
                task.address_line2?.toLowerCase().includes(searchLower) ||
                task.postcode?.toLowerCase().includes(searchLower);
              
              if (!matchesSearch) return false;
            }
            
            // Engineer filter
            if (engineerFilter === 'all') {
              return true;
            } else {
              return task.assigned_to === engineerFilter;
            }
          });
          
          if (filteredTasks.length === 0) {
            const getEngineerFilterMessage = () => {
              let message = '';
              if (engineerFilter === 'all') {
                message = "No archived tasks found";
              } else {
                message = "No archived tasks found for the selected engineer";
              }
              
              if (searchTerm.trim()) {
                message += ` matching "${searchTerm}"`;
              }
              
              return message + '.';
            };
            
            return (
              <div className="card" style={{ textAlign: 'center', padding: '3rem' }}>
                <h3>No archived tasks</h3>
                <p style={{ color: '#666', marginBottom: '2rem' }}>
                  {getEngineerFilterMessage()}
                </p>
                {searchTerm.trim() && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="btn btn-primary"
                    style={{ marginTop: '1rem' }}
                  >
                    Clear search
                  </button>
                )}
              </div>
            );
          }

          // Group filtered tasks by scheduled_date
          const groupedTasks = filteredTasks.reduce((groups: { [key: string]: Task[] }, task) => {
            const date = task.scheduled_date || 'No Date';
            if (!groups[date]) {
              groups[date] = [];
            }
            groups[date].push(task);
            return groups;
          }, {});

          // Sort dates (No Date last, then most recent first for archived tasks)
          const sortedDates = Object.keys(groupedTasks).sort((a, b) => {
            if (a === 'No Date') return 1;
            if (b === 'No Date') return -1;
            // For archived tasks, show most recent first
            const parseLocalDate = (dateStr: string) => {
              const parts = dateStr.split('-');
              return new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
            };
            return parseLocalDate(b).getTime() - parseLocalDate(a).getTime();
          });

          const formatDate = (dateString: string) => {
            if (dateString === 'No Date') return 'No Date';
            const dateParts = dateString.split('-');
            const date = new Date(parseInt(dateParts[0]), parseInt(dateParts[1]) - 1, parseInt(dateParts[2]));
            return date.toLocaleDateString('en-GB', { 
              weekday: 'long', 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            });
          };

          const formatTime = (timeString: string) => {
            if (!timeString) return '';
            
            if (timeString.includes('T')) {
              const timePart = timeString.split('T')[1];
              if (timePart.includes(':')) {
                const parts = timePart.split(':');
                return `${parts[0]}:${parts[1]}`;
              }
              return timePart;
            }
            
            if (timeString.includes(':')) {
              const parts = timeString.split(':');
              return `${parts[0]}:${parts[1]}`;
            }
            
            return timeString;
          };

          return (
            <div className="task-list-by-date">
              {sortedDates.map(date => {
                const isCollapsed = collapsedDates.has(date);
                return (
                  <div key={date} className="date-group" style={{ marginBottom: '2rem' }}>
                    <h3 
                      className="date-header" 
                      onClick={() => toggleDateCollapse(date)}
                      style={{ 
                        fontSize: '1.25rem', 
                        fontWeight: 'bold', 
                        color: '#2c3e50',
                        marginBottom: '0',
                        padding: '0.75rem',
                        backgroundColor: '#f8f9fa',
                        borderLeft: '4px solid #6c757d',
                        borderRadius: isCollapsed ? '4px' : '4px 4px 0 0',
                        cursor: 'pointer',
                        userSelect: 'none',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <span>
                        📋 {formatDate(date)} ({groupedTasks[date].length} archived {groupedTasks[date].length === 1 ? 'task' : 'tasks'})
                      </span>
                      <span style={{ 
                        fontSize: '1rem',
                        transform: isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s ease'
                      }}>
                        ▼
                      </span>
                    </h3>
                    
                    {!isCollapsed && (
                      <div className="task-list" style={{ 
                        backgroundColor: 'white',
                        border: '1px solid #e9ecef',
                        borderTop: 'none',
                        borderRadius: '0 0 8px 8px',
                        overflow: 'hidden'
                      }}>
                        {groupedTasks[date].map((task, index) => (
                          <div 
                            key={task.id} 
                            className="task-list-item" 
                            style={{ 
                              padding: '0.75rem',
                              borderBottom: index < groupedTasks[date].length - 1 ? '1px solid #e9ecef' : 'none',
                              display: 'flex',
                              flexDirection: 'column',
                              gap: '0.5rem',
                              backgroundColor: '#f8f9fa',
                              opacity: 0.8
                            }}
                          >
                            {/* Task Header */}
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '0.5rem' }}>
                              <div>
                                <h4 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 'bold', color: '#2c3e50' }}>
                                  {task.title}
                                </h4>
                                <p style={{ margin: '0.25rem 0 0 0', color: '#6c757d', fontSize: '0.9rem' }}>
                                  {task.client}
                                </p>
                              </div>
                              
                              {/* Status and Invoice Number */}
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', alignItems: 'flex-end' }}>
                                <span style={{ 
                                  fontSize: '0.875rem', 
                                  color: 'white',
                                  backgroundColor: '#6c757d',
                                  padding: '0.25rem 0.5rem',
                                  borderRadius: '4px',
                                  fontWeight: 'bold'
                                }}>
                                  INVOICED
                                </span>
                                {task.invoice_number && (
                                  <span style={{ fontSize: '0.75rem', color: '#666' }}>
                                    Invoice: {task.invoice_number}
                                  </span>
                                )}
                              </div>
                            </div>

                            {/* Address */}
                            <div style={{ fontSize: '0.9rem', color: '#495057' }}>
                              <div>{task.address_line1}</div>
                              {task.address_line2 && <div>{task.address_line2}</div>}
                              <div>{task.postcode}</div>
                            </div>

                            {/* Time */}
                            {(task.start_time || task.end_time) && (
                              <div style={{ 
                                fontSize: '0.9rem', 
                                color: '#3498db', 
                                fontWeight: '500' 
                              }}>
                                🕒 {task.start_time ? formatTime(task.start_time) : ''} 
                                {task.start_time && task.end_time && ' - '}
                                {task.end_time ? formatTime(task.end_time) : ''}
                              </div>
                            )}

                            {/* Description */}
                            {task.description && (
                              <div style={{ 
                                fontSize: '0.9rem', 
                                color: '#495057',
                                backgroundColor: 'white',
                                padding: '0.5rem',
                                borderRadius: '4px',
                                borderLeft: '3px solid #6c757d'
                              }}>
                                {task.description}
                              </div>
                            )}

                            {/* Engineer Notes */}
                            {task.engineer_notes && (
                              <div style={{ 
                                fontSize: '0.9rem', 
                                color: '#495057',
                                backgroundColor: '#e8f4f8',
                                padding: '0.5rem',
                                borderRadius: '4px',
                                borderLeft: '3px solid #17a2b8'
                              }}>
                                <div style={{ marginBottom: shouldTruncateNotes(task.engineer_notes) && !expandedNotes.has(task.id) ? '0.5rem' : '0' }}>
                                  <strong style={{ color: '#17a2b8' }}>Engineer Notes:</strong>{' '}
                                  {expandedNotes.has(task.id) || !shouldTruncateNotes(task.engineer_notes) 
                                    ? task.engineer_notes 
                                    : getTruncatedNotes(task.engineer_notes)
                                  }
                                </div>
                                {shouldTruncateNotes(task.engineer_notes) && (
                                  <button
                                    onClick={() => toggleNotesExpansion(task.id)}
                                    style={{
                                      background: 'none',
                                      border: 'none',
                                      color: '#17a2b8',
                                      fontSize: '0.875rem',
                                      cursor: 'pointer',
                                      textDecoration: 'underline',
                                      padding: '0',
                                      fontWeight: '500'
                                    }}
                                  >
                                    {expandedNotes.has(task.id) ? 'Read less' : 'Read more'}
                                  </button>
                                )}
                              </div>
                            )}

                            {/* Assignment */}
                            <div style={{ fontSize: '0.875rem', color: '#6c757d' }}>
                              {task.assigned_user ? 
                                `👤 Assigned to: ${task.assigned_user.first_name} ${task.assigned_user.last_name}` : 
                                '👤 Unassigned'
                              }
                            </div>

                            {/* Action Buttons */}
                            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.5rem', justifyContent: 'flex-end' }}>
                              <button 
                                onClick={() => handleViewPhotos(task.id)} 
                                className="btn btn-success"
                                style={{ fontSize: '0.875rem', padding: '0.4rem 0.8rem' }}
                              >
                                📷 Photos
                              </button>
                              
                              {userIsAdmin && (
                                <button 
                                  onClick={() => handleEditTask(task)} 
                                  className="btn btn-primary"
                                  style={{ fontSize: '0.875rem', padding: '0.4rem 0.8rem' }}
                                >
                                  Edit
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })()}

        {userIsAdmin && (
          <TaskModal
            isOpen={isModalOpen}
            onClose={() => {
              setIsModalOpen(false);
              setEditingTask(undefined);
            }}
            onSubmit={handleTaskSubmit}
            task={editingTask}
            users={users}
          />
        )}

        <PhotoUpload
          taskId={selectedTaskId || 0}
          isOpen={isPhotoModalOpen}
          onClose={() => {
            setIsPhotoModalOpen(false);
            setSelectedTaskId(null);
          }}
        />
      </div>
    </div>
  );
};

export default Archived;